// ──────────────────────────────────────────────────────────────────────────
// DiarioStatisticheFiltri.js — Barra filtri condivisi tra i sotto-tab
// della pagina Statistiche del Diario.
//
// Refactor v131 sub-deploy 3:
//   - 3 dropdown affiancati: Stagione · Periodo · Sistema
//     (filtro Tipo rimosso per decisione utente)
//   - "Personalizzato" del Periodo: appare una sotto-riga con 2 input data
//   - Banner "Filtri attivi" con × per Reset (D101 v129)
// ──────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import { useStatisticheFiltri } from "../contexts/StatisticheFiltriContext";

import { API } from "../config";
const FONT = "'DM Sans', sans-serif";

const VOCI_PERIODO = [
  { id: "tutta_stagione",   label: "Tutta la stagione" },
  { id: "anno_solare",      label: "Anno solare" },
  { id: "ultimi_12m",       label: "Ultimi 12 mesi" },
  { id: "ultimo_semestre",  label: "Ultimo semestre" },
  { id: "ultimo_trimestre", label: "Ultimo trimestre" },
  { id: "ultimo_mese",      label: "Ultimo mese" },
  { id: "tutto",            label: "Tutto" },
  { id: "personalizzato",   label: "Personalizzato" },
];
const LABEL_PERIODO_BY_ID = Object.fromEntries(VOCI_PERIODO.map(v => [v.id, v.label]));

function etichettaStagione(stag) {
  if (!stag) return "—";
  const et = stag.etichetta || "";
  let pretty = et;
  const m = /^(\d{4})\/(\d{4})$/.exec(et);
  if (m) pretty = `${m[1].slice(2)}-${m[2].slice(2)}`;
  if (!stag.data_fine) pretty += " (in corso)";
  return pretty;
}

// ── Dropdown generico riusabile ───────────────────────────────────────────

function Dropdown({ label, valori, attivo, onChange, larghezzaMin = 160, disabilitato }) {
  const [aperto, setAperto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!aperto) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAperto(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [aperto]);

  const labelAttivo = valori.find(v => v.id === attivo)?.label
    || (attivo === null ? valori[0]?.label : "");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{
        fontSize: 11, fontWeight: 600, color: "#64748B",
        textTransform: "uppercase", letterSpacing: 0.5,
      }}>{label}</span>
      <div ref={ref} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => !disabilitato && setAperto(v => !v)}
          disabled={disabilitato}
          style={{
            background: "#fff",
            color: disabilitato ? "#CBD5E1" : "#1E293B",
            border: "1px solid #E2E8F0", borderRadius: 8,
            padding: "8px 32px 8px 14px", fontSize: 13, fontWeight: 500,
            fontFamily: FONT,
            cursor: disabilitato ? "not-allowed" : "pointer",
            minWidth: larghezzaMin, textAlign: "left",
            position: "relative", whiteSpace: "nowrap",
          }}
        >
          {labelAttivo || "—"}
          <span style={{
            position: "absolute", right: 12, top: "50%",
            transform: "translateY(-50%)", color: "#94A3B8", fontSize: 9,
          }}>▼</span>
        </button>
        {aperto && !disabilitato && (
          <div style={{
            position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 60,
            minWidth: larghezzaMin, maxHeight: 320, overflowY: "auto",
            background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8,
            boxShadow: "0 8px 24px rgba(15,23,42,0.10)", padding: 4,
          }}>
            {valori.map(v => {
              const sel = attivo === v.id;
              return (
                <button
                  key={v.id ?? "_null"}
                  type="button"
                  onClick={() => { onChange(v.id); setAperto(false); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "8px 12px",
                    background: sel ? "#EFF6FF" : "transparent",
                    color: sel ? "#2563EB" : "#1E293B",
                    border: "none", borderRadius: 4, cursor: "pointer",
                    fontSize: 13, fontWeight: sel ? 600 : 500,
                    fontFamily: FONT, whiteSpace: "nowrap",
                  }}
                >{v.label}</button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sotto-riga "Personalizzato" (input Da/A) ─────────────────────────────

function CustomRangePicker({ stagioneRif, daData, aData, onApplica }) {
  const oggi = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  })();
  const defaultDa = stagioneRif?.data_inizio || oggi;

  const [valDa, setValDa] = useState(daData || defaultDa);
  const [valA, setValA] = useState(aData || oggi);

  useEffect(() => {
    setValDa(daData || defaultDa);
    setValA(aData || oggi);
  }, [daData, aData]); // eslint-disable-line react-hooks/exhaustive-deps

  const valido = valDa && valA && valDa <= valA;

  return (
    <div style={{
      marginTop: 8, padding: "10px 16px",
      background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8,
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Da:</label>
        <input
          type="date"
          value={valDa || ""}
          onChange={(e) => setValDa(e.target.value)}
          style={{
            padding: "6px 10px", fontSize: 13, fontFamily: FONT,
            border: "1px solid #E2E8F0", borderRadius: 6,
            color: "#1E293B", background: "#fff",
          }}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>A:</label>
        <input
          type="date"
          value={valA || ""}
          onChange={(e) => setValA(e.target.value)}
          style={{
            padding: "6px 10px", fontSize: 13, fontFamily: FONT,
            border: "1px solid #E2E8F0", borderRadius: 6,
            color: "#1E293B", background: "#fff",
          }}
        />
      </div>
      <button
        type="button"
        disabled={!valido}
        onClick={() => onApplica(valDa, valA)}
        style={{
          background: valido ? "#2563EB" : "#CBD5E1",
          color: "#fff", border: "none", borderRadius: 6,
          padding: "7px 14px", fontSize: 12, fontWeight: 600,
          cursor: valido ? "pointer" : "not-allowed",
          fontFamily: FONT,
        }}
      >Applica</button>
      {!valido && (
        <span style={{ fontSize: 11, color: "#DC2626" }}>
          La data "Da" deve essere precedente o uguale alla data "A".
        </span>
      )}
    </div>
  );
}

// ── Componente principale ────────────────────────────────────────────────

export default function DiarioStatisticheFiltri() {
  const filtri = useStatisticheFiltri();
  const [stagioni, setStagioni] = useState([]);
  const [sistemiDisponibili, setSistemiDisponibili] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  useEffect(() => {
    let annullato = false;
    setLoadingMeta(true);
    Promise.all([
      fetch(`${API}/diario/stagioni`).then(r => r.json()).catch(() => []),
      fetch(`${API}/diario/size-per-sistema`).then(r => r.json()).catch(() => []),
    ]).then(([stags, sys]) => {
      if (annullato) return;
      setStagioni(Array.isArray(stags) ? stags : []);
      setSistemiDisponibili(Array.isArray(sys) ? sys : []);
    }).finally(() => {
      if (!annullato) setLoadingMeta(false);
    });
    return () => { annullato = true; };
  }, []);

  const vociStagione = [
    { id: null, label: "Stagione corrente" },
    ...stagioni.map(s => ({ id: s.id, label: etichettaStagione(s) })),
  ];

  const vociSistema = [
    { id: null, label: "Tutti i sistemi" },
    ...sistemiDisponibili.map(s => ({ id: s.sistema_id, label: s.nome_sistema })),
  ];

  const descrizioniAttivi = [];
  if (filtri.stagione_id) {
    const s = stagioni.find(x => x.id === filtri.stagione_id);
    descrizioniAttivi.push(`stagione ${etichettaStagione(s)}`);
  }
  if (filtri.periodo !== "tutta_stagione") {
    descrizioniAttivi.push(LABEL_PERIODO_BY_ID[filtri.periodo] || filtri.periodo);
  }
  if (filtri.sistema_id) {
    const nome = sistemiDisponibili.find(x => x.sistema_id === filtri.sistema_id)?.nome_sistema || filtri.sistema_id;
    descrizioniAttivi.push(`sistema ${nome}`);
  }

  const stagioneSel = filtri.stagione_id
    ? stagioni.find(s => s.id === filtri.stagione_id)
    : (stagioni.find(s => !s.data_fine) || stagioni[0]);

  return (
    <div style={{ marginBottom: 16, fontFamily: FONT }}>
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "flex-start",
        gap: 16, padding: "14px 16px",
        background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
      }}>
        <Dropdown
          label="Stagione"
          valori={vociStagione}
          attivo={filtri.stagione_id}
          onChange={filtri.setStagione}
          larghezzaMin={170}
          disabilitato={loadingMeta}
        />
        <Dropdown
          label="Periodo"
          valori={VOCI_PERIODO}
          attivo={filtri.periodo}
          onChange={filtri.setPeriodo}
          larghezzaMin={180}
        />
        <Dropdown
          label="Sistema"
          valori={vociSistema}
          attivo={filtri.sistema_id}
          onChange={filtri.setSistema}
          larghezzaMin={180}
          disabilitato={loadingMeta}
        />
      </div>

      {filtri.periodo === "personalizzato" && (
        <CustomRangePicker
          stagioneRif={stagioneSel}
          daData={filtri.da_data_personalizzato}
          aData={filtri.a_data_personalizzato}
          onApplica={filtri.setDatePersonalizzate}
        />
      )}

      {filtri.hasFiltriAttivi && (
        <div style={{
          marginTop: 8, padding: "8px 14px",
          background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 8,
        }}>
          <div style={{ fontSize: 12, color: "#1E3A8A" }}>
            <strong>Filtri attivi:</strong> {descrizioniAttivi.join(" · ")}
          </div>
          <button
            type="button"
            onClick={filtri.resetTutti}
            style={{
              background: "transparent", border: "1px solid #2563EB",
              color: "#2563EB", borderRadius: 6, padding: "4px 12px",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
              fontFamily: FONT,
            }}
          >× Reset tutti i filtri</button>
        </div>
      )}
    </div>
  );
}
